/**
 * Mixin de `APIChat` (`apiChat.js`) — Modo Agente: ciclo de vida do run
 * (aceitar/cancelar/reidratar/retry), cursor de reconexão e decisão de
 * proposta (spec-002-chat-agentico-estilo-claude-code, `plan.md` D13).
 *
 * Extraído por tamanho (`CLAUDE.md`: arquivos ≤ 500 linhas). Contrato
 * conferido contra `views/chat/view-chat-modo-agente.mixin.js` — os nomes
 * de getter/mutation abaixo têm que bater byte a byte com o que lá se lê.
 *
 * Handlers dos eventos SSE moraram aqui até 1.4.9 — viraram
 * `apiChatModoAgenteEventos.js` quando o teto foi cruzado; os dois mixins se
 * fundem no MESMO prototype via `Object.assign` (`apiChat.js`).
 */
import store from '@state/store'
import con from '@services/connection'
import { iaCrmRequest, respostaDeCancelamento } from '@modules/aawz-intelligence-v2/utils/api'
import { intelligenceV2 } from '@modules/aawz-intelligence-v2/constants/URL'
import {
  STREAM_HEADER,
  isStreamResponse,
  gerarUuidV4,
  calcularBackoffMs,
} from '@modules/aawz-intelligence-v2/api/sseParser'

/** `POST /runs` recusado porque a conversa já tem um run ativo (contrato `api.yaml`). */
const STATUS_CONVERSA_OCUPADA = 409

// Backoff da reconexão por cursor ([adv C-30]): no máximo 5 tentativas — a
// varredura de `run-atual` já diria "não há mais run" antes disso.
const MAX_TENTATIVAS_RECONEXAO_EOF = 5
// [T043-3] Prazo para o relay entregar `estado{cancelled}` + `end{cancelado}`
// pelo MESMO stream após o `POST cancelar` responder 200. Só dispara o
// fallback (abortar + reidratar por `runAtual`) se o `end` não chegou nesse
// intervalo — o caminho feliz nunca precisa dele.
const TIMEOUT_FALLBACK_CANCELAR_MS = 10000
// [T043-4] Estados terminais da máquina de estados do run (US-13) — reidratar
// um run terminal é só o snapshot; reabrir o relay por cursor não faz
// sentido (não há mais eventos vindo, o turno já fechou).
const ESTADOS_TERMINAIS_RUN = new Set(['done', 'failed', 'cancelled', 'expired'])

export const apiChatModoAgenteMixin = {
  /**
   * Avança o cursor do Modo Agente (`_runAtualSeq`/store `runAtual.seq`) só
   * depois do GRUPO de frames completo — [adv C-11 codex]. Cada frame do par
   * dual compartilha o MESMO `seq` (é o mesmo evento canônico, `relay.py::
   * gerar_frames`); `frameIndex`/`frameCount` ausentes = evento single-emit,
   * avança direto. Nunca regride (uma reconexão pode reenviar o grupo).
   */
  _avancarCursorSeNecessario({ seq, frameIndex, frameCount }) {
    if (seq === null || seq === undefined) return
    const grupoCompleto = frameCount === null || frameIndex === frameCount - 1
    if (!grupoCompleto) return
    if (seq <= this._runAtualSeq) return
    this._runAtualSeq = seq
    // O cursor da INSTÂNCIA avança sempre (é ele que reconecta); o do slot
    // fixo só quando o slot é deste run (`_slotDoRunEhMeu`, review #2364).
    if (!this._slotDoRunEhMeu()) return
    this._dispatchNoStoreDoAgente('setRunSeq', seq)
  },

  /** `setTimeout` isolado num método para o backoff ser mockável em teste. */
  _aguardar(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms))
  },

  /**
   * Conecta ao relay SSE por cursor (`GET /runs/{id_run}/eventos?desde=`) e
   * processa via a MESMA infra de streaming do Modo Chat (`_executeStream`,
   * hoje dentro da classe — `apiChatStream.js` é código morto desde o merge do
   * PR #2242).
   *
   * ⚠️ O stream é reivindicado pelo caminho CANÔNICO — `_abrirAbortController`,
   * que aborta o anterior, incrementa `_streamSeq` e devolve o número DESTE —,
   * e esse número é repassado ao `_executeStream`. Entre o merge do PR #2242
   * (16/09/2026) e este commit, o método atribuía `this.abortController` direto
   * e chamava `_executeStream(response)` sem o 2º argumento: `_streamAtivo(
   * undefined)` é SEMPRE falso, então o relay saía por `respostaDeCancelamento()`
   * logo após a leitura e pulava TUDO o que vem depois — `_flushTokenBuffer`,
   * `_avisarQueOSaldoMudou`, a reconexão por EOF com o run ainda vivo
   * (`_reconectarPorEofSeRunAberto`), o fecho anômalo (`_fechouSemDesfecho`) e o
   * `setChatLoading(false)` final; no `catch`, o desfecho da queda de conexão.
   * Sintoma medido em produção local: o `POST /runs` saía, o servidor executava
   * a tool e finalizava o run, e a TELA FICAVA PARADA. O `_executeStream` veio
   * de `desenvolvimento` já com a assinatura de dois parâmetros ("um stream por
   * vez", 15/09/2026); este mixin, que vive fora daquele arquivo, não foi
   * atualizado junto — é assim que um merge quebra o que compila.
   *
   * @param {string} idRun
   * @param {number=} desde Último `seq` recebido (0/omitido = do início).
   * @returns {Promise<Object>}
   */
  async _acompanharRun(idRun, desde) {
    const meuStream = this._abrirAbortController()
    const response = await iaCrmRequest({
      method: 'GET',
      endpoint: intelligenceV2.runEventos(idRun, desde),
      customHeaders: STREAM_HEADER,
      options: { signal: this.abortController.signal },
    })
    // Substituído enquanto os headers vinham (o consultor mandou outra
    // mensagem): o envelope de cancelamento que `iaCrmRequest` devolve não pode
    // derrubar o `chatLoading` do turno novo — a MESMA guarda de
    // `_pedirEDrenar`, pelo mesmo motivo.
    if (!this._streamAtivo(meuStream)) return respostaDeCancelamento()
    if (!isStreamResponse(response)) {
      store.dispatch(this.storeNamespace + '/setChatLoading', false)
      return response || {}
    }
    return this._executeStream(response, meuStream)
  },

  /**
   * [adv C-30] EOF sem `end`/`error`: consulta `run-atual` e reconecta por
   * cursor com backoff (1 s, 2 s, 4 s, ... teto 30 s), no máximo 5 tentativas.
   * Fire-and-forget — chamado de `_executeStream` sem `await` do turno atual.
   * `reconectando` (STORE fixo) liga o banner do wireframe W3 durante toda a
   * tentativa (`view-chat.vue::reconectando`).
   *
   * [VERIFY-1/M-5] Guard `_reconexaoEofEmAndamento`: sem ele, um relay que
   * cai repetidamente multiplica laços concorrentes — `_executeStream` agenda
   * OUTRO laço a cada EOF aninhado enquanto este continua tentando, e cada
   * um sobrescreve `this.abortController` do anterior. `finally` libera
   * mesmo se o laço sair por `break`/exceção.
   */
  async _agendarReconexaoPorEof() {
    if (this._reconexaoEofEmAndamento) return
    this._reconexaoEofEmAndamento = true
    this._dispatchNoStoreDoAgente('setReconectando', true)
    try {
      for (let tentativa = 0; tentativa < MAX_TENTATIVAS_RECONEXAO_EOF; tentativa++) {
        if (this._terminalRecebido || this._isAborted()) break
        await this._aguardar(calcularBackoffMs(tentativa))
        if (this._terminalRecebido || this._isAborted()) break
        const deveParar = await this._tentarReconectarPorCursorNoEof()
        if (deveParar) break
      }
    } finally {
      this._dispatchNoStoreDoAgente('setReconectando', false)
      this._reconexaoEofEmAndamento = false
    }
  },

  /**
   * Uma tentativa dentro do laço de `_agendarReconexaoPorEof` — extraído só
   * pra manter o laço legível (`max-statements`). Retorna `true` quando o
   * laço deve parar (sem run ativo, ou o terminal já chegou pela reconexão).
   *
   * [VERIFY-1/M-4] `reconectarPorCursor` (não `_acompanharRun` direto) — já
   * despacha `setChatLoading(true)` antes de reabrir o stream; sem isso,
   * `showStreamingMessage` (=`chatLoading`) ficava `false` durante toda a
   * reconexão e o texto reconectado não pintava na tela até o `end`.
   * [VERIFY-1/C-77] `this._runAtualSeq` aqui já é o cursor REALMENTE
   * aplicado (não a cauda do servidor) — `runAtual` não sobrescreve mais
   * esse campo.
   */
  async _tentarReconectarPorCursorNoEof() {
    try {
      const r = await this.runAtual(this._conversationIdAtual)
      const valor = r && r.value
      if (!valor || !valor.id_run) return true
      await this.reconectarPorCursor(valor.id_run, this._runAtualSeq)
      return Boolean(this._terminalRecebido)
    } catch (err) {
      console.warn('[IA CRM] Falha na reconexão por cursor (EOF sem terminal):', err)
      return false
    }
  },

  /**
   * Aceita um turno no Modo Agente (UC-1): `POST /runs` (síncrono) seguido —
   * se aceito — da conexão ao relay SSE do run recém-criado.
   *
   * @param {Object} opts
   * @param {string} opts.id_conversa    Conversa existente (omitido cria nova).
   * @param {string} opts.mensagem       Texto enviado ao agente.
   * @param {Array=} opts.anexos         `[{id_objeto}]`.
   * @param {string=} opts.candidato     `A|B` (bandit A/B — E-40).
   * @param {string=} opts.agent_slug    Especialista escolhido na tela (#172).
   * @returns {Promise<Object>} `Envelope` (`status` 200/402/403/409/503).
   *
   * ★ [ponte "a tela escolhe o agente", owner 16/09/2026 — backend PR #1787]
   * `agent_slug` faz o run rodar com o CATÁLOGO daquele especialista. Ausente
   * ⇒ o corpo fica idêntico ao de hoje e o run nasce no agente default. Slug
   * inválido ⇒ o backend recusa com motivo NOMEADO (400/403), nunca cai no
   * default em silêncio — o silêncio é o defeito que esta ponte fecha.
   *
   * O RETRY não repassa o slug de propósito: `POST /runs/{id}/retry` reabre a
   * entrada do run anterior e o backend herda a escolha dali
   * (`aceitar_run.py`: `agent_slug=entrada.get('agent_slug')`). Mandar de novo
   * daqui seria uma segunda fonte para o mesmo campo.
   */
  async aceitarRun({ id_conversa, mensagem, anexos, candidato, agent_slug } = {}) {
    this.resetChatState()
    this._dispatchNoStoreDoAgente('resetPropostasAbertas') // [VERIFY-3h] turno novo nao herda cards do anterior
    store.dispatch(this.storeNamespace + '/addMessage', { id: null, role: 'user', content: mensagem })

    const r = await con.post(
      intelligenceV2.runs,
      this._buildAceitarRunBody({ id_conversa, mensagem, anexos, candidato, agent_slug }),
      false, { json: true }
    )
    if (!r || r.status !== 200 || !r.value) return this._runRecusado(r)

    const idConversaEfetiva = this._fixarConversaDoRun(r.value, id_conversa)
    this._aplicarRunAceito(r.value, idConversaEfetiva)
    if (r.value.id_run) {
      await this._acompanharRun(r.value.id_run, 0)
    }
    return r
  },

  /**
   * O `POST /runs` que não deu 200 — método próprio só para caber no
   * `max-statements` do ESLint (14); a régua do repo é extrair, nunca
   * desabilitar a regra.
   *
   * ★ [19/09/2026] O 409 é a ÚNICA recusa que NÃO encerra o turno: ele diz que
   * esta conversa JÁ tem um run correndo, e o stream dele pode estar aberto
   * agora. Derrubar o `chatLoading` aqui apagava o "Pensando..." de um turno
   * que continua em voo — medido na tela: a mensagem entrava, a bolha sumia e
   * nada mais acontecia. Quem decide o estado nesse caso é a reidratação
   * (`view-chat-modo-agente.mixin.js::tratarRunEmAndamento`), que já sabe
   * distinguir run terminal de run vivo.
   */
  _runRecusado(r) {
    if (!r || r.status !== STATUS_CONVERSA_OCUPADA) {
      store.dispatch(this.storeNamespace + '/setChatLoading', false)
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  /**
   * [VERIFY-1/C-76] Sem conversa selecionada, `POST /runs` cria uma nova
   * (contrato 1.4.2+, `value.id_conversa`) — seleciona no store ANTES de abrir
   * o relay, com o mesmo shape mínimo que `_onEventStart` já usa pro Modo
   * Chat. Não espera o `start` do SSE: a resposta síncrona já garante o id, e
   * depender só do evento deixaria uma janela onde `enviarMensagem` já chamou
   * `carregarConversas()` sem a conversa nova selecionada.
   */
  _fixarConversaDoRun(valor, idConversaPedida) {
    const idConversaEfetiva = valor.id_conversa || idConversaPedida
    if (!idConversaPedida && idConversaEfetiva) {
      store.dispatch(this.storeNamespace + '/setSelectedConversation', {
        id: idConversaEfetiva,
      })
    }
    return idConversaEfetiva
  },

  _buildAceitarRunBody({ id_conversa, mensagem, anexos, candidato, agent_slug }) {
    const body = { id_conversa: id_conversa || null, mensagem: mensagem, client_request_id: gerarUuidV4() }
    if (anexos && anexos.length) body.anexos = anexos
    if (candidato) body.candidato = candidato
    // Só quando HÁ escolha: sem o seletor (ou com a rota de agentes
    // indisponível) o corpo tem exatamente as chaves de sempre, e o backend
    // segue pelo caminho antigo byte a byte.
    if (agent_slug) body.agent_slug = agent_slug
    return body
  },

  /** Guarda o `id_run` recém-aceito localmente e espelha no store. */
  _aplicarRunAceito(valor, idConversa) {
    this._runAtualIdRun = valor.id_run || null
    this._runAtualSeq = 0
    // [VERIFY-1/C-78] Run novo = dedupe novo — o watermark do run anterior
    // não tem nenhuma relação com o seq (baixo) deste.
    this._ultimoFrameAplicado = null
    if (idConversa) this._conversationIdAtual = idConversa
    this._dispatchNoStoreDoAgente('setRunAtual', {
      id_run: valor.id_run, estado: valor.estado || 'queued', seq: 0, motivo: null,
      // A conversa dona do run — é o que recorta o chip por aba
      // (`utils/run-da-conversa.js`).
      id_conversa: idConversa || valor.id_conversa || null,
    })
  },

  /**
   * Reconecta ao acompanhamento de um run por cursor (UC-8). Fallback: sem
   * `id_run` conhecido (nem passado, nem em `_runAtualIdRun`), cai no
   * `reconnect()` legado por `conversation_id` (Modo Chat).
   *
   * @param {string=} idRun
   * @param {number=} desde
   * @returns {Promise<Object>}
   */
  reconectarPorCursor(idRun, desde) {
    const runIdEfetivo = idRun || this._runAtualIdRun
    if (!runIdEfetivo) {
      return this.reconnect({ id: this._conversationIdAtual })
    }
    const cursor = (desde === undefined || desde === null) ? this._runAtualSeq : desde
    store.dispatch(this.storeNamespace + '/setChatLoading', true)
    return this._acompanharRun(runIdEfetivo, cursor)
  },

  /**
   * `GET /conversas/{id}/run-atual` — reidratação após reload/nova aba
   * ([adv C-24]). Posicional (e não `{id_conversa}`) por alinhamento com
   * `cancelarRun(idRun)`/`decidirProposta(idRun, ...)`.
   *
   * [VERIFY-1/C-77] NÃO escreve em `_runAtualSeq` — `r.value.seq` é a CAUDA
   * do servidor, não o cursor aplicado (só `_avancarCursorSeNecessario`
   * escreve nele). **[VERIFY-3/F-1]** `value: null` limpa o store
   * (`resetRunAtual`) — senão o `runAtual` da conversa ANTERIOR sobrevivia
   * na tela desta. **[VERIFY-3/Q-4]** repassa `retry_permitido`/`motivo` do
   * snapshot (contrato 1.4.12) — antes zerados aqui, "Tentar de novo" só
   * reaparecia depois de um evento `estado` novo.
   *
   * **[T313]** O envelope traz DUAS listas de proposta: `propostas_abertas`
   * (o que ainda aceita decisão) e `propostas` (o RELATÓRIO do run — toda
   * proposta com a decisão dela, US-8 AC3). As duas são aplicadas; sem a
   * segunda, o relatório do lote sumia num F5.
   *
   * @param {string} idConversa
   * @returns {Promise<Object>}
   */
  async runAtual(idConversa) {
    if (!idConversa) {
      return this.buildErrorResponse({ message: 'id_conversa obrigatório', status: 400 })
    }
    const r = await con.get(intelligenceV2.conversaRunAtual(idConversa))
    if (r && r.status === 200) {
      if (r.value) {
        this._runAtualIdRun = r.value.id_run || null
        this._dispatchNoStoreDoAgente('setRunAtual', {
          id_run: r.value.id_run, estado: r.value.estado, seq: r.value.seq,
          motivo: r.value.motivo, retry_permitido: r.value.retry_permitido,
          id_conversa: idConversa,
        })
        // [T313] O relatório do run PRIMEIRO (o que aconteceu), as abertas
        // depois (o que ainda aceita decisão) — ver `_aplicarRelatorioDoRun`.
        this._aplicarRelatorioDoRun(r.value.propostas)
        this._aplicarPropostasAbertas(r.value.propostas_abertas)
      } else {
        this._runAtualIdRun = null
        this._dispatchNoStoreDoAgente('resetRunAtual')
      }
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  /**
   * [T043-4] Reidratação completa do run (US-5): snapshot + reconecta ao
   * relay por cursor se NÃO terminal. Sem a 2ª metade, chip/mensagens
   * congelam no reload e só "descongelam" com mensagem nova do consultor.
   *
   * @param {string} idConversa
   * @returns {Promise<Object>} O mesmo `Envelope` de `runAtual`.
   */
  async reidratarRun(idConversa) {
    // Guard precisa do estado ANTES de `runAtual()` (sobrescreve `_runAtualIdRun`).
    const streamJaAtivo = this._streamEmAndamento()
    const idRunAntes = this._runAtualIdRun
    const seqAplicadoAntes = this._runAtualSeq

    const r = await this.runAtual(idConversa)
    const valor = r && r.value
    if (valor && valor.id_run) {
      // [VERIFY-1/C-77] `valor.seq` (cauda) pularia o não aplicado.
      const mesmoRunDeAntes = idRunAntes === valor.id_run
      const cursorAplicado = this._cursorParaReidratacao(mesmoRunDeAntes, seqAplicadoAntes)

      if (ESTADOS_TERMINAIS_RUN.has(valor.estado)) {
        await this._drenarBacklogTerminal(valor, cursorAplicado)
      // Só reconecta se o stream vivo NÃO era do mesmo run do snapshot — um
      // run DIFERENTE (ex.: anterior ainda drenando) não bloqueia o atual.
      } else if (!streamJaAtivo || !mesmoRunDeAntes) {
        await this.reconectarPorCursor(valor.id_run, cursorAplicado)
      }
    }
    return r
  },

  // [VERIFY-3k/Q-11] Run terminal com backlog drena UMA vez pelo cursor (inclusive F5).
  // É replay, não turno: sem "Pensando...", sem reconexão por EOF e sem bolha de
  // erro se o log não trouxer `end` — o loading sempre cai no fim.
  //
  // ★ [owner, 21/09/2026] O `finally` derrubava o loading SEM perguntar de quem
  // ele era. Medido pela sonda na tela: F5 numa conversa cujo último run está
  // `done` abre este replay; o consultor manda mensagem antes de ele terminar;
  // o turno novo (`aceitarRun` → 403 → `sendMessage`) ABORTA o replay pelo
  // `_abrirAbortController`, o `await` abaixo volta, e o `finally` apagava o
  // "Pensando..." do turno NOVO — a resposta só aparecia pronta, no `end`.
  // `_acompanharRun` reivindica o stream ANTES do primeiro `await`, então o
  // número lido logo depois da chamada é o DESTE replay: se ele já não é o
  // ativo, outro turno mandou no loading, e este não toca nele.
  async _drenarBacklogTerminal(valor, cursorAplicado) {
    if (cursorAplicado >= valor.seq) return
    this._drenandoRunTerminal = true
    const drenagem = this._acompanharRun(valor.id_run, cursorAplicado)
    const meuStream = this._streamSeq
    try {
      await drenagem
    } finally {
      this._drenandoRunTerminal = false
      if (this._streamAtivo(meuStream)) store.dispatch(this.storeNamespace + '/setChatLoading', false)
    }
  },

  /**
   * `desde=` da reconexão em `reidratarRun`: cursor aplicado quando é o
   * MESMO run que a instância já vinha acompanhando; `0` quando é um run
   * diferente (ou instância nova) — nesse caso também zera `_runAtualSeq` e
   * o watermark de dedupe (`_ultimoFrameAplicado`), porque a projeção do run
   * ANTERIOR não serve de piso pro seq (baixo) do run novo: sem isso,
   * `_avancarCursorSeNecessario` travava e o dedupe de frame (C-78) barrava
   * os primeiros eventos legítimos.
   */
  _cursorParaReidratacao(mesmoRunDeAntes, seqAplicadoAntes) {
    if (mesmoRunDeAntes) return seqAplicadoAntes
    this._runAtualSeq = 0
    this._ultimoFrameAplicado = null
    return 0
  },

  /**
   * Guard contra stream duplicado: só é "em andamento" quando há um reader
   * de verdade lendo agora — `abortController` sozinho não basta (ele existe
   * durante toda a vida do `fetch`, inclusive já abortado).
   *
   * @returns {boolean}
   */
  _streamEmAndamento() {
    return Boolean(this.activeReader && !this._isAborted())
  },

  /**
   * Decide uma proposta (UC-5): aprovar/rejeitar/editar. `decision_id`
   * garante que um duplo clique não commita duas vezes. **[VERIFY-3/F-2]**
   * `opts.decision_id` opcional: retry do "Tentar novamente" reusa o MESMO
   * id (replay idempotente, `resultado: 'replay'` se já gravou); ausente
   * gera um novo.
   *
   * @param {string} idRun
   * @param {string} idProposta
   * @param {Object} opts `{decisao, hash_payload, payload_editado?, decision_id?}`.
   * @returns {Promise<Object>} `Envelope{resultado, recibo}`.
   */
  async decidirProposta(idRun, idProposta, { decisao, hash_payload, payload_editado, decision_id } = {}) {
    if (!idRun || !idProposta) {
      return this.buildErrorResponse({ message: 'idRun e idProposta são obrigatórios', status: 400 })
    }
    const body = { decisao: decisao, hash_payload: hash_payload, decision_id: decision_id || gerarUuidV4() }
    if (payload_editado) body.payload_editado = payload_editado

    const r = await con.post(intelligenceV2.runDecisao(idRun, idProposta), body, false, { json: true })
    if (r && r.status === 200 && r.value) {
      this._aplicarResultadoDecisao(idProposta, r.value)
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  /**
   * [VERIFY-1/C-80] `editar` fecha a proposta atual e ABRE UMA NOVA
   * (`resultado: 'nova_proposta'`, `contracts/api.yaml` exemplo `editada`)
   * — o relay publica o `tool-approval-request` da substituta na MESMA
   * transação (upsert completo, com preview, via `_onToolApprovalRequest`
   * de sempre), mas a resposta SÍNCRONA de `decidirProposta` já garante
   * `id_proposta_nova`/`hash_payload` sem esperar o SSE: invalida a antiga
   * e registra a nova de imediato. Se o preview/resumo não vier no corpo,
   * o upsert do evento ENRIQUECE a mesma entrada depois (merge raso de
   * `UPSERT_PROPOSTA`) — nada se perde. Extraído de `decidirProposta` só
   * pra manter a complexidade ciclomática dela sob o teto do lint.
   */
  _aplicarResultadoDecisao(idProposta, valor) {
    if (valor.resultado === 'nova_proposta' && valor.id_proposta_nova) {
      this._dispatchNoStoreDoAgente('invalidarProposta', {
        id_proposta: idProposta, substituida_por: valor.id_proposta_nova,
      })
      this._dispatchNoStoreDoAgente('upsertProposta', {
        id_proposta: valor.id_proposta_nova,
        hash_payload: valor.hash_payload || null,
        estado: 'aberta',
        preview: valor.preview || {
          tool: null, entidade: null, operacao: null, alvo: null,
          resumo: valor.resumo || null,
        },
      })
      return
    }
    this._dispatchNoStoreDoAgente('resolverProposta', {
      id_proposta: idProposta, resultado: valor.resultado, recibo: valor.recibo || null,
    })
  },

  /**
   * Cancela o run (UC-9): notifica o backend e DEIXA o relay SSE aberto —
   * [T043-3] abortar o stream aqui (como antes) matava a conexão ANTES do
   * gateway publicar `estado{cancelled}` + `end{cancelado}` (com a mensagem
   * de fechamento "Turno cancelado pelo consultor."), que então nunca
   * chegavam ao front: `_onEventEnd` — quem fecha o turno de verdade
   * (`setChatLoading(false)`, `addMessage` da mensagem de fechamento) — não
   * rodava por esse caminho. Deixando o stream vivo, o `end` chega pelo
   * relay como em qualquer encerramento normal.
   *
   * @param {string} idRun
   * @returns {Promise<Object>} `Envelope{estado, efeitos_conhecidos}`.
   */
  /**
   * ESTA instância é quem acompanha o run `idRun`?
   *
   * ⚠️ [20/09/2026] O slice do Modo Agente (`runAtual`) vive no namespace
   * FIXO e é ÚNICO para todas as conversas vivas — dívida herdada do #2349.
   * Sem esta pergunta, "Pedir nova proposta" no cartão expirado de uma aba
   * cancelava o run que estava rodando em OUTRA.
   */
  acompanhaRun(idRun) {
    return Boolean(idRun) && this._runAtualIdRun === idRun
  },

  async cancelarRun(idRun) {
    if (!idRun) {
      return this.buildErrorResponse({ message: 'idRun obrigatório', status: 400 })
    }
    const r = await con.post(intelligenceV2.runCancelar(idRun))
    if (r && r.status === 200 && r.value) {
      this._dispatchNoStoreDoAgente('setRunEstado', { estado: r.value.estado, motivo: null })
      // Fire-and-forget — não atrasa o retorno deste método (mesmo padrão de
      // `_agendarReconexaoPorEof`, `_executeStream`).
      this._agendarFallbackDeCancelamento(idRun)
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  /**
   * [T043-3] Fallback do cancelamento: o `POST cancelar` respondeu 200, mas
   * se o `end` não chegar pelo relay em `TIMEOUT_FALLBACK_CANCELAR_MS`
   * (worker morto sem publicar o terminal), aborta o stream local e reidrata
   * via `runAtual` — o mesmo desfecho que o código antigo forçava sempre,
   * agora só como último recurso.
   *
   * @param {string} idRun O run que foi cancelado — se o consultor já saiu
   *   dele (nova conversa, outro run) antes do timeout, o fallback não age.
   */
  async _agendarFallbackDeCancelamento(idRun) {
    await this._aguardar(TIMEOUT_FALLBACK_CANCELAR_MS)
    if (this._terminalRecebido || this._isAborted()) return
    if (this._runAtualIdRun !== idRun) return
    await this._abortarStreamAtivo()
    this._clearTokenBuffer()
    this._resetarFase()
    store.dispatch(this.storeNamespace + '/setChatLoading', false)
    if (this._conversationIdAtual) {
      await this.runAtual(this._conversationIdAtual)
    }
  },

  /**
   * [VERIFY-2/G-D6b] `GET /runs/{id_run}` (`contracts/api.yaml`) — snapshot
   * pontual usado SÓ pra ler `custo_creditos`: nem o fio SSE (`estado`/
   * `end`) nem `runAtual`/`run-atual` carregam esse campo até o chip
   * (`ui-components.md` §2 não tem prop de custo). `view-chat.vue` chama
   * uma vez, ao entrar em `done`.
   *
   * @param {string} idRun
   * @returns {Promise<Object>} `Envelope{id_run, estado, seq, propostas_abertas, efeitos_conhecidos, custo_creditos}`.
   */
  async getRun(idRun) {
    if (!idRun) {
      return this.buildErrorResponse({ message: 'idRun obrigatório', status: 400 })
    }
    const r = await con.get(intelligenceV2.run(idRun))
    if (r && r.status === 200 && r.value) {
      this._dispatchNoStoreDoAgente('setRunCustoCreditos', r.value.custo_creditos)
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  /**
   * [VERIFY-2/G-D6c] "Tentar de novo" do chip (W2, estado `failed`) —
   * `POST /runs/{id_run}/retry` com `client_request_id` novo (idempotência
   * do clique, mesmo padrão de `_buildAceitarRunBody`). Sucesso (200): o run
   * novo (`retry_of` = `idRun`) vira `runAtual` e o relay reconecta do zero
   * — mesmo tratamento de `aceitarRun` via `_aplicarRunAceito` +
   * `_acompanharRun`, só que sem `addMessage` (não há texto novo do
   * consultor). 409 ("Run não retomável.") é responsabilidade do chamador
   * (`view-chat.vue::onTentarDeNovo`): mostra a mensagem e esconde o botão.
   *
   * @param {string} idRun Run `failed` atual.
   * @returns {Promise<Object>} `Envelope{id_run, retry_of}` (200) ou 409.
   */
  async retryRun(idRun) {
    if (!idRun) {
      return this.buildErrorResponse({ message: 'idRun obrigatório', status: 400 })
    }
    const r = await con.post(
      intelligenceV2.runRetry(idRun),
      { client_request_id: gerarUuidV4() },
      false, { json: true }
    )
    if (r && r.status === 200 && r.value && r.value.id_run) {
      this.resetChatState()
      this._aplicarRunAceito(r.value, this._conversationIdAtual)
      await this._acompanharRun(r.value.id_run, 0)
    }
    return r || this.buildErrorResponse({ message: 'Erro inesperado' })
  },

  // [VERIFY-3/F-1] Encerra o acompanhamento do run ATUAL ao trocar de
  // conversa: aborta o stream ativo e limpa `runAtual`. PÚBLICO de
  // propósito — `_abortarStreamAtivo` é privado, o componente não chama.
  async pararAcompanhamentoDoRun() {
    await this._abortarStreamAtivo()
    this._runAtualIdRun = null
    this._dispatchNoStoreDoAgente('resetRunAtual')
  },
}
